import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Grid,
  Chip,
  Stack
} from '@mui/material'
import ConfirmDialog from '../../../shared/dialogs/ConfirmDialog'
import NoticeBar from '../../../shared/NoticeBar'
import { AdminFormSection, ToggleField } from '../../shared/fields'
import { apiClient, dataApi } from '../../../../services/api'
import { poolwerxColors } from '../../../../theme/colors'
import { formatNumber } from '../../../../utils/formatting'
import { getCountryChipSx } from '../../../../utils/countryChips'
import { LightspeedIcon, XeroIcon, SkimmerIcon } from '../../../SystemIcons'

interface FranchisePartner {
  id: number
  fp_code: string
  franchise_name: string
  has_complete_lightspeed_connection?: boolean
  has_complete_xero_connection?: boolean
  has_complete_skimmer_connection?: boolean
}

interface SystemOption {
  id: string
  label: string
  icon: React.ReactElement
}

interface ClearDataDialogProps {
  open: boolean
  onClose: () => void
  franchisePartner: FranchisePartner | null
  franchisePartners?: FranchisePartner[]
  isBulkOperation?: boolean
  selectedFranchises?: Set<string>
  onSuccess: (message: string, triggerSync: boolean) => void
  onError: (message: string) => void
}

const ClearDataDialog: React.FC<ClearDataDialogProps> = ({
  open,
  onClose,
  franchisePartner,
  franchisePartners = [],
  isBulkOperation = false,
  selectedFranchises = new Set(),
  onSuccess,
  onError
}) => {
  const [clearReason, setClearReason] = useState('')
  const [selectedSystems, setSelectedSystems] = useState<string[]>([])
  const [systemDataCounts, setSystemDataCounts] = useState<{[key: string]: any}>({})
  const [loadingDataCounts, setLoadingDataCounts] = useState<{[key: string]: boolean}>({})
  const [runSyncAfterClear, setRunSyncAfterClear] = useState(true)
  const [clearDialogError, setClearDialogError] = useState<string | null>(null)
  const [clearOperationInProgress, setClearOperationInProgress] = useState(false)

  // Available systems for clearing
  const availableSystems: SystemOption[] = [
    {
      id: 'lightspeed',
      label: 'Lightspeed',
      icon: <LightspeedIcon sx={{ width: 20, height: 20 }} />
    },
    {
      id: 'xero',
      label: 'Xero',
      icon: <XeroIcon sx={{ width: 20, height: 20 }} />
    },
    {
      id: 'skimmer',
      label: 'Skimmer',
      icon: <SkimmerIcon sx={{ width: 20, height: 20 }} />
    }
  ]

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setClearReason('')
      setSelectedSystems([])
      setSystemDataCounts({})
      setLoadingDataCounts({})
      setRunSyncAfterClear(true)
      setClearDialogError(null)
      setClearOperationInProgress(false)
    }
  }, [open])

  // Fetch data counts for selected systems
  const fetchDataCounts = async (fpCode: string) => {
    try {
      const response = await apiClient.get(`/admin/franchise-partners/${fpCode}/data-counts`)
      return response.data
    } catch (error: any) {
      console.error('Failed to fetch data counts:', error)
      return null
    }
  }

  // Filter data counts by system
  const filterDataCountsBySystem = (data: any, systemId: string) => {
    if (!data || !data.breakdown) return null

    // Define which data types belong to each system
    const systemDataTypes: {[key: string]: string[]} = {
      lightspeed: [
        'products', 'sales', 'customers', 'outlets', 'registers',
        'payment_types', 'taxes', 'inventory', 'product_categories',
        'sale_line_items', 'sale_payments', 'users', 'suppliers',
        'tags', 'consignments', 'tax_rates'
      ],
      xero: [
        'contacts', 'invoices', 'payments', 'credit_notes',
        'credit_note_line_items', 'credit_note_allocations',
        'manual_journals', 'manual_journal_line_items'
      ],
      skimmer: []
    }

    const relevantTypes = systemDataTypes[systemId] || []
    const filteredBreakdown: {[key: string]: number} = {}
    let totalRecords = 0

    relevantTypes.forEach(type => {
      if (data.breakdown[type]) {
        filteredBreakdown[type] = data.breakdown[type]
        totalRecords += data.breakdown[type]
      }
    })

    return {
      ...data,
      breakdown: filteredBreakdown,
      total_records: totalRecords
    }
  }

  // Load data counts when systems are selected
  useEffect(() => {
    if (!franchisePartner || isBulkOperation) return

    selectedSystems.forEach(async (systemId) => {
      if (!systemDataCounts[systemId] && !loadingDataCounts[systemId]) {
        setLoadingDataCounts(prev => ({ ...prev, [systemId]: true }))
        const data = await fetchDataCounts(franchisePartner.fp_code)
        if (data) {
          const filteredData = filterDataCountsBySystem(data, systemId)
          setSystemDataCounts(prev => ({ ...prev, [systemId]: filteredData }))
        }
        setLoadingDataCounts(prev => ({ ...prev, [systemId]: false }))
      }
    })
  }, [selectedSystems, franchisePartner, isBulkOperation])

  // Get connected systems for single FP
  const getConnectedSystems = (): SystemOption[] => {
    if (!franchisePartner) return []

    const systems: SystemOption[] = []
    if (franchisePartner.has_complete_lightspeed_connection) {
      systems.push(availableSystems.find(s => s.id === 'lightspeed')!)
    }
    if (franchisePartner.has_complete_xero_connection) {
      systems.push(availableSystems.find(s => s.id === 'xero')!)
    }
    // Note: Skimmer is intentionally excluded even if connected - not live yet
    // Uncomment when Skimmer goes live:
    // if (franchisePartner.has_complete_skimmer_connection) {
    //   systems.push(availableSystems.find(s => s.id === 'skimmer')!)
    // }

    return systems
  }

  const capitalizeSystemName = (systemName: string) => {
    return systemName.charAt(0).toUpperCase() + systemName.slice(1)
  }

  const clearFranchiseData = async () => {
    // ConfirmDialog gates the "CLEAR" text, the reason, and >=1 system before
    // this fires (see confirmText / confirmDisabled below). Only the FP
    // sanity check remains as a defensive guard.
    if (!isBulkOperation && !franchisePartner) {
      setClearDialogError('Invalid franchise partner. Please refresh the page and try again.')
      return
    }

    try {
      setClearOperationInProgress(true)
      setClearDialogError(null)

      let response: any

      if (isBulkOperation) {
        // Bulk operation for multiple franchises
        console.log(`Clearing data for ${selectedFranchises.size} franchises`)

        response = await dataApi.bulkClearData({
          fp_codes: Array.from(selectedFranchises),
          systems: selectedSystems,
          reason: clearReason,
          confirm_text: 'CLEAR',
          dry_run: false,
          trigger_sync: runSyncAfterClear
        })

        const { successful_franchises, failed_franchises, message } = response.data

        if (successful_franchises > 0) {
          onSuccess(`✅ ${message}`, runSyncAfterClear)
        } else {
          onError(`❌ ${message}`)
        }

        // Log detailed results for debugging
        if (response.data.results) {
          console.log('Bulk operation results:', response.data.results)
        }
      } else {
        // Single franchise operation
        const fpCode = franchisePartner!.fp_code
        console.log(`Clearing data for systems [${selectedSystems.join(', ')}] for ${fpCode}`)

        if (selectedSystems.length === 1) {
          // Use single system endpoint
          response = await dataApi.clearSystemData(selectedSystems[0], fpCode, {
            reason: clearReason,
            confirm_deletion: true,
            dry_run: false,
            trigger_sync: runSyncAfterClear
          })
        } else {
          // Use multi-system endpoint
          response = await dataApi.clearMultiSystemData(fpCode, {
            systems: selectedSystems,
            reason: clearReason,
            confirm_deletion: true,
            dry_run: false,
            trigger_sync: runSyncAfterClear
          })
        }

        const fpName = franchisePartner!.franchise_name || fpCode.toUpperCase()
        const totalRecords = response.data.total_records_cleared || 0

        let successMsg = `✅ Data cleared successfully for ${fpName}`
        if (totalRecords > 0) {
          successMsg += ` (${totalRecords} records)`
        }
        if (selectedSystems.length > 1) {
          const capitalizedSystems = selectedSystems.map(capitalizeSystemName)
          successMsg += ` - Systems: ${capitalizedSystems.join(', ')}`
        } else {
          successMsg += ` - System: ${capitalizeSystemName(selectedSystems[0])}`
        }

        onSuccess(successMsg, runSyncAfterClear)
        console.log('Clear data response:', response.data)
      }

      // Close dialog on success
      onClose()
    } catch (error: any) {
      const errorContext = isBulkOperation
        ? `bulk operation for ${selectedFranchises.size} franchises`
        : franchisePartner?.fp_code || 'unknown'
      console.error(`Failed to clear data for ${errorContext}:`, error)

      // Better error message extraction for dialog
      let errorMessage = 'Unknown error occurred'
      if (error.response?.data?.detail) {
        // Handle 409 conflicts (active sync) specially
        if (error.response.status === 409 && typeof error.response.data.detail === 'object') {
          const detail = error.response.data.detail
          let message = detail.message || detail.error || 'Active sync detected'

          // Capitalize system names in the error message
          if (detail.active_systems && Array.isArray(detail.active_systems)) {
            const capitalizedSystems = detail.active_systems.map(capitalizeSystemName)
            const systemsPattern = detail.active_systems.join(', ')
            const capitalizedSystemsPattern = capitalizedSystems.join(', ')
            message = message.replace(systemsPattern, capitalizedSystemsPattern)
          }

          errorMessage = message
          if (detail.suggestion) {
            errorMessage += `. ${detail.suggestion}`
          }
        } else {
          errorMessage = typeof error.response.data.detail === 'string'
            ? error.response.data.detail
            : error.response.data.detail.message || 'Error occurred'
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      } else if (typeof error === 'string') {
        errorMessage = error
      }

      // Set error in dialog (don't close it)
      setClearDialogError(errorMessage)

      // Only show toast for non-conflict errors
      if (error.response?.status !== 409) {
        if (isBulkOperation) {
          onError(`Failed to clear data for selected franchises: ${errorMessage}`)
        } else {
          const fpName = franchisePartner?.franchise_name || franchisePartner?.fp_code?.toUpperCase() || 'franchise'
          onError(`Failed to clear data for ${fpName}: ${errorMessage}`)
        }
      }
    } finally {
      setClearOperationInProgress(false)
    }
  }

  const handleClose = () => {
    if (!clearOperationInProgress) {
      onClose()
    }
  }

  return (
    <ConfirmDialog
      open={open}
      onClose={handleClose}
      onConfirm={clearFranchiseData}
      tone="danger"
      size="md"
      title="Clear System Data"
      pending={clearOperationInProgress}
      confirmLabel={
        isBulkOperation
          ? `Clear Data for ${selectedFranchises.size} Franchises`
          : 'Clear Data'
      }
      confirmText="CLEAR"
      confirmTextLabel="To permanently clear this data, type CLEAR to confirm"
      confirmDisabled={selectedSystems.length === 0 || !clearReason.trim()}
      errorText={clearDialogError}
      body={
        <Grid container spacing={2}>
          {/* Target franchise(s) */}
          <AdminFormSection
            card
            title={isBulkOperation ? 'Franchises' : 'Franchise'}
            action={
              isBulkOperation ? (
                <Chip
                  label={`${selectedFranchises.size} franchise${selectedFranchises.size !== 1 ? 's' : ''}`}
                  size="small"
                  sx={{ bgcolor: poolwerxColors.teal, color: 'white', fontWeight: 600 }}
                />
              ) : undefined
            }
          >
            {isBulkOperation ? (
              <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                <Grid container spacing={1}>
                  {Array.from(selectedFranchises).map((fpCode) => {
                    const fp = franchisePartners.find((f) => f.fp_code === fpCode)
                    const country = fpCode.slice(0, 2).toUpperCase()
                    return (
                      <Grid item xs={12} sm={6} key={fpCode}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            bgcolor: 'background.paper',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            {fp?.franchise_name || fpCode}
                          </Typography>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={country}
                            sx={{ ...getCountryChipSx(country), flexShrink: 0 }}
                          />
                        </Box>
                      </Grid>
                    )
                  })}
                </Grid>
              </Box>
            ) : (
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {franchisePartner?.franchise_name || franchisePartner?.fp_code?.toUpperCase()}
              </Typography>
            )}
          </AdminFormSection>

          {/* System selection */}
          <AdminFormSection card title="Systems to clear">

            {isBulkOperation ? (
              // For bulk operations: Show all systems (except Skimmer which isn't live)
              <Grid container spacing={2}>
                {availableSystems.filter(s => s.id !== 'skimmer').map((system) => (
                  <SystemCheckbox
                    key={system.id}
                    system={system}
                    isSelected={selectedSystems.includes(system.id)}
                    onToggle={(checked) => {
                      if (checked) {
                        setSelectedSystems(prev => [...prev, system.id])
                      } else {
                        setSelectedSystems(prev => prev.filter(s => s !== system.id))
                        setSystemDataCounts(prev => {
                          const newCounts = { ...prev }
                          delete newCounts[system.id]
                          return newCounts
                        })
                        setLoadingDataCounts(prev => {
                          const newLoading = { ...prev }
                          delete newLoading[system.id]
                          return newLoading
                        })
                      }
                    }}
                    showDataCounts={!isBulkOperation}
                    dataLoading={loadingDataCounts[system.id]}
                    dataCounts={systemDataCounts[system.id]}
                  />
                ))}
              </Grid>
            ) : (
              // For single FP: Check connections
              getConnectedSystems().length === 0 ? (
                <NoticeBar severity="warning">
                  No connected systems available for clearing. This franchise partner does not
                  have complete connections to any systems.
                </NoticeBar>
              ) : (
                <Grid container spacing={2}>
                  {getConnectedSystems().map((system) => (
                    <SystemCheckbox
                      key={system.id}
                      system={system}
                      isSelected={selectedSystems.includes(system.id)}
                      onToggle={(checked) => {
                        if (checked) {
                          setSelectedSystems(prev => [...prev, system.id])
                        } else {
                          setSelectedSystems(prev => prev.filter(s => s !== system.id))
                          setSystemDataCounts(prev => {
                            const newCounts = { ...prev }
                            delete newCounts[system.id]
                            return newCounts
                          })
                          setLoadingDataCounts(prev => {
                            const newLoading = { ...prev }
                            delete newLoading[system.id]
                            return newLoading
                          })
                        }
                      }}
                      showDataCounts={!isBulkOperation}
                      dataLoading={loadingDataCounts[system.id]}
                      dataCounts={systemDataCounts[system.id]}
                    />
                  ))}
                </Grid>
              )
            )}
          </AdminFormSection>

          {/* Reason */}
          <AdminFormSection card title="Reason for clearing">
            <TextField
              fullWidth
              required
              value={clearReason}
              onChange={(e) => setClearReason(e.target.value)}
              placeholder="e.g., Testing fresh sync, Data corruption fix"
              multiline
              rows={2}
              error={!clearReason.trim() && selectedSystems.length > 0}
              helperText={
                !clearReason.trim() && selectedSystems.length > 0 ? 'Reason is required' : ''
              }
            />
          </AdminFormSection>

          {/* Post-clear option */}
          <AdminFormSection card title="After clearing">
            <ToggleField
              label="Run sync for cleared systems after clearing"
              checked={runSyncAfterClear}
              onChange={setRunSyncAfterClear}
              disabled={selectedSystems.length === 0}
              activeColor="success"
            />
          </AdminFormSection>
        </Grid>
      }
    />
  )
}

// Helper component for system checkbox with data counts
interface SystemCheckboxProps {
  system: SystemOption
  isSelected: boolean
  onToggle: (checked: boolean) => void
  showDataCounts: boolean
  dataLoading?: boolean
  dataCounts?: any
}

const SystemCheckbox: React.FC<SystemCheckboxProps> = ({
  system,
  isSelected,
  onToggle,
  showDataCounts,
  dataLoading,
  dataCounts
}) => {
  return (
    <Grid item xs={12}>
      <Box
        sx={{
          border: '1px solid',
          borderColor: isSelected ? poolwerxColors.teal : 'divider',
          borderRadius: 1,
          p: 2,
          bgcolor: isSelected ? 'rgba(0, 127, 152, 0.05)' : 'transparent',
          transition: 'all 0.2s ease-in-out',
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={isSelected}
              onChange={(e) => onToggle(e.target.checked)}
              sx={{
                color: poolwerxColors.teal,
                '&.Mui-checked': {
                  color: poolwerxColors.teal,
                },
              }}
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {system.icon}
              <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                {system.label}
              </Typography>
            </Box>
          }
          sx={{
            alignItems: 'center',
            marginLeft: 0,
            '& .MuiCheckbox-root': {
              padding: '9px'
            }
          }}
        />

        {/* Show data counts when system is selected */}
        {isSelected && showDataCounts && (
          <Box sx={{ mt: 2, ml: 4 }}>
            {dataLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Loading data counts...
                </Typography>
              </Box>
            ) : dataCounts ? (
              <Box>
                {/* Data type chips */}
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2, gap: 1 }}>
                  {(() => {
                    const dataItems: React.ReactElement[] = []

                    // Map the breakdown data to chips
                    if (dataCounts.breakdown) {
                      Object.entries(dataCounts.breakdown).forEach(([key, value]: [string, any]) => {
                        if (value > 0) {
                          // Format the key for display
                          const label = key
                            .replace(/_/g, ' ')
                            .replace(/\b\w/g, (l: string) => l.toUpperCase())

                          dataItems.push(
                            <Chip
                              key={key}
                              label={`${label}: ${formatNumber(value)}`}
                              size="small"
                              variant="outlined"
                              sx={{
                                borderColor: poolwerxColors.teal,
                                color: poolwerxColors.teal,
                                '& .MuiChip-label': {
                                  fontSize: '0.75rem',
                                  fontWeight: 500
                                }
                              }}
                            />
                          )
                        }
                      })
                    }

                    return dataItems.length > 0 ? dataItems : (
                      <Typography variant="body2" color="text.secondary">
                        No data to clear
                      </Typography>
                    )
                  })()}
                </Stack>

                {/* Total count summary */}
                <Box sx={{
                  p: 1.5,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Total records to clear:
                  </Typography>
                  <Typography variant="body1" sx={{
                    fontWeight: 700,
                    color: dataCounts.total_records > 0 ? 'error.main' : 'text.secondary'
                  }}>
                    {formatNumber(dataCounts.total_records || 0)}
                  </Typography>
                </Box>
              </Box>
            ) : null}
          </Box>
        )}
      </Box>
    </Grid>
  )
}

export default ClearDataDialog